import { onBeforeUnmount, ref } from 'vue'

type StreamTarget = 'draft' | 'embedded'

type Options = {
  getText: (target: StreamTarget) => string
  setText: (target: StreamTarget, value: string) => void
  intervalMs?: number
  minCharsPerTick?: number
  maxCharsPerTick?: number
}

export function useStreamedEditor(options: Options) {
  const activeStreamTarget = ref<StreamTarget>('draft')
  const draftEditorWaitingMessage = ref('')
  const embeddedEditorWaitingMessage = ref('')
  const streamRenderBuffer = ref('')
  const streamRenderTimer = ref<number | null>(null)
  const streamHasVisibleText = ref(false)
  const streamRenderWaiters: Array<() => void> = []

  const intervalMs = options.intervalMs ?? 36
  const minCharsPerTick = options.minCharsPerTick ?? 1
  const maxCharsPerTick = options.maxCharsPerTick ?? 2

  const getStreamText = () => options.getText(activeStreamTarget.value)

  const setStreamText = (value: string) => {
    options.setText(activeStreamTarget.value, value)
  }

  const getWaitingMessage = () => (activeStreamTarget.value === 'draft' ? draftEditorWaitingMessage.value : embeddedEditorWaitingMessage.value)

  const setWaitingMessage = (value: string) => {
    if (activeStreamTarget.value === 'draft') {
      draftEditorWaitingMessage.value = value
      return
    }
    embeddedEditorWaitingMessage.value = value
  }

  const resolveStreamRenderWaiters = () => {
    while (streamRenderWaiters.length) {
      streamRenderWaiters.shift()?.()
    }
  }

  const stopStreamRenderer = () => {
    if (streamRenderTimer.value !== null) {
      window.clearInterval(streamRenderTimer.value)
      streamRenderTimer.value = null
    }
  }

  const drainStreamBuffer = () => {
    if (!streamRenderBuffer.value) {
      stopStreamRenderer()
      resolveStreamRenderWaiters()
      return
    }

    const charsPerTick = Math.min(
      maxCharsPerTick,
      Math.max(minCharsPerTick, Math.ceil(streamRenderBuffer.value.length / 80))
    )
    setStreamText(getStreamText() + streamRenderBuffer.value.slice(0, charsPerTick))
    streamRenderBuffer.value = streamRenderBuffer.value.slice(charsPerTick)

    if (!streamRenderBuffer.value) {
      stopStreamRenderer()
      resolveStreamRenderWaiters()
    }
  }

  const ensureStreamRenderer = () => {
    if (streamRenderTimer.value !== null) {
      return
    }

    streamRenderTimer.value = window.setInterval(() => {
      drainStreamBuffer()
    }, intervalMs)
  }

  const resetStreamRenderer = () => {
    stopStreamRenderer()
    streamRenderBuffer.value = ''
    streamHasVisibleText.value = false
    resolveStreamRenderWaiters()
  }

  const startStreamingIntoEditor = () => {
    resetStreamRenderer()
    setWaitingMessage('')
    setStreamText('')
  }

  const showWaitingMessageInEditor = (message: string) => {
    resetStreamRenderer()
    setWaitingMessage(message)
    setStreamText(message)
  }

  const clearWaitingMessageInEditor = () => {
    const waitingMessage = getWaitingMessage()
    if (waitingMessage && getStreamText() === waitingMessage) {
      setStreamText('')
    }
    setWaitingMessage('')
  }

  const enqueueStreamingText = (text: string) => {
    if (!text) {
      return
    }

    clearWaitingMessageInEditor()

    if (!streamHasVisibleText.value) {
      const immediateChars = Math.min(18, text.length)
      setStreamText(getStreamText() + text.slice(0, immediateChars))
      streamHasVisibleText.value = true
      streamRenderBuffer.value += text.slice(immediateChars)
      ensureStreamRenderer()
      return
    }

    streamRenderBuffer.value += text
    ensureStreamRenderer()
  }

  const replaceStreamingText = (content: string) => {
    startStreamingIntoEditor()
    enqueueStreamingText(content)
  }

  const waitForStreamingRender = async () => {
    if (!streamRenderBuffer.value && streamRenderTimer.value === null) {
      return
    }

    await new Promise<void>((resolve) => {
      streamRenderWaiters.push(resolve)
      ensureStreamRenderer()
    })
  }

  onBeforeUnmount(() => {
    resetStreamRenderer()
  })

  return {
    activeStreamTarget,
    draftEditorWaitingMessage,
    embeddedEditorWaitingMessage,
    resetStreamRenderer,
    clearWaitingMessageInEditor,
    showWaitingMessageInEditor,
    enqueueStreamingText,
    replaceStreamingText,
    waitForStreamingRender,
  }
}